import { toISODate, type Task } from "@todoer/shared";

export const fmtDuration = (m: number): string =>
  m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}`;

export const dayDate = (iso: string) => new Date(`${iso}T00:00`);
export const addDaysISO = (iso: string, n: number) => { const d = dayDate(iso); d.setDate(d.getDate() + n); return toISODate(d); };
export const shortDate = (d: string) => dayDate(d).toLocaleDateString([], { day: "numeric", month: "short" });
export const weekdayShort = (d: string) => dayDate(d).toLocaleDateString([], { weekday: "short" });
export const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export type Tone = "overdue" | "today" | "tomorrow" | "soon" | "later";
export const toneClass: Record<Tone, string> = {
  overdue: "text-danger", today: "text-success", tomorrow: "text-warning", soon: "text-text-muted", later: "text-text-muted",
};

/** "Today", "Tomorrow", "Fri", "Mon 2 Nov" for a calendar date relative to today. */
export function friendlyDate(d: string, today = toISODate(new Date())): string {
  if (d === today) return "Today";
  if (d === addDaysISO(today, 1)) return "Tomorrow";
  if (d === addDaysISO(today, -1)) return "Yesterday";
  const date = dayDate(d);
  if (d > today && d < addDaysISO(today, 7)) return date.toLocaleDateString([], { weekday: "long" });
  const sameYear = date.getFullYear() === dayDate(today).getFullYear();
  return date.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
}

export function dueLabel(task: Pick<Task, "due">, today = toISODate(new Date())): { text: string; tone: Tone; cls: string } | null {
  if (!task.due) return null;
  const d = task.due.date;
  const time = task.due.datetime ? ` ${fmtTime(task.due.datetime)}` : "";
  let tone: Tone = "later";
  let text: string;
  if (d < today) { tone = "overdue"; text = d === addDaysISO(today, -1) ? "Yesterday" : shortDate(d); }
  else if (d === today) { tone = "today"; text = "Today"; }
  else if (d === addDaysISO(today, 1)) { tone = "tomorrow"; text = "Tomorrow"; }
  else { tone = d < addDaysISO(today, 7) ? "soon" : "later"; text = friendlyDate(d, today); }
  return { text: `${text}${time}`, tone, cls: toneClass[tone] };
}

/** "just now", "5 min ago", "2 h ago", "yesterday", "3 Oct". */
export function relativeTime(iso: string, now = Date.now()): string {
  const diff = Math.round((now - new Date(iso).getTime()) / 1000);
  if (diff < 45) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86_400) return `${Math.round(diff / 3600)} h ago`;
  const d = toISODate(new Date(iso));
  if (d === addDaysISO(toISODate(new Date(now)), -1)) return "yesterday";
  if (diff < 7 * 86_400) return new Date(iso).toLocaleDateString([], { weekday: "long" });
  return new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" });
}
